import * as XLSX from "xlsx";
import type { CostEstimateData } from "./types";
import {
  type CostEstimateResult,
  itemBreakdown,
  majorWorkBreakdown,
  mergeWorks,
  midWorkBreakdown,
  formatRate,
} from "./calc";

export interface ExportMeta {
  projectName: string;
  vendorName: string;
  dateStr: string;
}

type Row = (string | number)[];

/** 미리보기 양식대로 시트가 몇 개인지 (버튼 라벨용) */
export const sheetCount = (data: CostEstimateData): number => {
  const majors = mergeWorks(data.directMaterial, data.directLabor);
  return 2 + majors.length + majors.reduce((n, m) => n + m.midWorks.length, 0);
};

/** 31자 이내 시트명으로 정리 (엑셀 제한) */
const safeSheetName = (name: string) =>
  name.replace(/[\\/?*[\]:]/g, " ").slice(0, 31);

function coverSheet(result: CostEstimateResult, meta: ExportMeta): Row[] {
  return [
    ["공사원가계산서"],
    [],
    ["공사명", meta.projectName || ""],
    ["공사금액", result.totalCost],
    ["공급자(업체명)", meta.vendorName || ""],
    ["작성일자", meta.dateStr],
  ];
}

function summarySheet(
  data: CostEstimateData,
  result: CostEstimateResult,
): Row[] {
  const rows: Row[] = [["비목", "금액", "비고"]];
  rows.push(["재료비"]);
  rows.push(["직접재료비", result.directMaterial, ""]);
  rows.push([
    "간접재료비",
    result.indirectMaterial,
    `직접재료비×${formatRate(data.indirectMaterialRate)}`,
  ]);
  rows.push(["[소계]", result.materialSubtotal, ""]);
  rows.push(["노무비"]);
  rows.push(["직접노무비", result.directLabor, ""]);
  rows.push([
    "간접노무비",
    result.indirectLabor,
    `직접노무비×${formatRate(data.indirectLaborRate)}`,
  ]);
  rows.push(["[소계]", result.laborSubtotal, ""]);
  rows.push(["경비"]);
  for (const exp of result.expenses) {
    rows.push([
      exp.name,
      exp.amount,
      `${exp.baseLabel}×${formatRate(exp.rate)}`,
    ]);
  }
  rows.push(["[소계]", result.expenseSubtotal, ""]);
  rows.push(["계", result.netCost, ""]);
  rows.push([
    "일반관리비",
    result.generalAdmin,
    `계×${formatRate(data.generalAdminRate)}`,
  ]);
  rows.push([
    "이윤",
    result.profit,
    `(노무+경비+일관)×${formatRate(data.profitRate)}`,
  ]);
  rows.push(["폐기물처리비", result.wasteDisposal, "합계제외"]);
  rows.push(["공급가액", result.supplyPrice, ""]);
  rows.push(["부가가치세", result.vat, `공급가액×${formatRate(data.vatRate)}`]);
  rows.push(["도급액/총공사비", result.contractPrice, result.totalCost]);
  return rows;
}

const AGG_HEADER: Row = [
  "품명",
  "규격",
  "단위",
  "수량",
  "재료비",
  "노무비",
  "경비",
  "합계",
  "비고",
];

/** 다중 시트 워크북을 만들어 즉시 다운로드 */
export function downloadExcel(
  data: CostEstimateData,
  result: CostEstimateResult,
  meta: ExportMeta,
) {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(coverSheet(result, meta)),
    safeSheetName("내역서"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(summarySheet(data, result)),
    safeSheetName("공사원가계산서"),
  );

  for (const major of mergeWorks(data.directMaterial, data.directLabor)) {
    // 대공사 집계표 (중공종별)
    const majorRows: Row[] = [
      [`${major.code} ${major.name} 공종별 집계표`],
      AGG_HEADER,
    ];
    for (const mid of major.midWorks) {
      const b = midWorkBreakdown(mid);
      majorRows.push([
        `${mid.code} ${mid.name}`,
        "",
        "식",
        1,
        b.material,
        b.labor,
        b.expense,
        b.total,
        "",
      ]);
    }
    const mb = majorWorkBreakdown(major);
    majorRows.push([
      "합계",
      "",
      "",
      "",
      mb.material,
      mb.labor,
      mb.expense,
      mb.total,
      "",
    ]);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(majorRows),
      safeSheetName(`${major.code} 집계표`),
    );

    // 중공종 집계표 (품목별)
    for (const mid of major.midWorks) {
      const midRows: Row[] = [
        [`${mid.code} ${mid.name} 공종별 집계표`],
        AGG_HEADER,
      ];
      for (const item of mid.items) {
        const b = itemBreakdown(item);
        midRows.push([
          item.name,
          "",
          item.unit,
          item.quantity,
          b.material,
          b.labor,
          b.expense,
          b.total,
          "",
        ]);
      }
      const tb = midWorkBreakdown(mid);
      midRows.push([
        "합계",
        "",
        "",
        "",
        tb.material,
        tb.labor,
        tb.expense,
        tb.total,
        "",
      ]);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(midRows),
        safeSheetName(`${mid.code} 집계표`),
      );
    }
  }

  const fileName = `${meta.projectName || "공사원가계산서"}.xlsx`;
  XLSX.writeFile(wb, fileName);
}
